import { Line, Bar, Doughnut } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { cn } from '../../../lib/utils'

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler
)

const COLORS = {
  sky:     '#0EA5E9',
  emerald: '#10B981',
  blue:    '#2563EB',
  indigo:  '#6366F1',
}

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'rgba(10,22,40,0.9)',
      borderColor: 'rgba(255,255,255,0.1)',
      borderWidth: 1,
      titleColor: '#fff',
      bodyColor: 'rgba(255,255,255,0.7)',
      padding: 12,
    },
  },
  scales: {
    x: {
      grid: { color: 'rgba(255,255,255,0.05)' },
      ticks: { color: 'rgba(255,255,255,0.4)', font: { size: 11 } },
    },
    y: {
      grid: { color: 'rgba(255,255,255,0.05)' },
      ticks: { color: 'rgba(255,255,255,0.4)', font: { size: 11 } },
    },
  },
}

// Chart.js-style dataset shape (used in views)
interface ChartDataset {
  label?: string
  data: number[]
  borderColor?: string | string[]
  backgroundColor?: string | string[]
  fill?: boolean
  tension?: number
  pointRadius?: number
  pointHoverRadius?: number
  borderRadius?: number
  borderWidth?: number
}

interface ChartData {
  labels: string[]
  datasets: ChartDataset[]
}

// ---- AreaChart ----
interface AreaChartProps {
  // Option A: Chart.js data object (preferred in views)
  data?: ChartData
  title?: string
  // Option B: flat props (backwards compat)
  labels?: string[]
  datasets?: { label: string; data: number[]; color?: string }[]
  height?: number
  className?: string
}

export function AreaChart({ data, title, labels, datasets, height = 200, className }: AreaChartProps) {
  const resolvedLabels = data?.labels ?? labels ?? []
  const resolvedDatasets = data
    ? data.datasets.map((d, i) => ({
        ...d,
        borderColor: d.borderColor ?? (i === 0 ? COLORS.sky : COLORS.emerald),
        backgroundColor: d.backgroundColor ?? `${i === 0 ? COLORS.sky : COLORS.emerald}20`,
        fill: d.fill ?? true,
        tension: d.tension ?? 0.4,
        pointRadius: d.pointRadius ?? 0,
        pointHoverRadius: d.pointHoverRadius ?? 4,
      }))
    : (datasets ?? []).map((d, i) => ({
        label: d.label,
        data: d.data,
        borderColor: d.color ?? (i === 0 ? COLORS.sky : COLORS.emerald),
        backgroundColor: `${d.color ?? (i === 0 ? COLORS.sky : COLORS.emerald)}20`,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
      }))

  const chart = (
    <div className={cn('w-full', !title && className)} style={{ height }}>
      <Line options={baseOptions as any} data={{ labels: resolvedLabels, datasets: resolvedDatasets }} />
    </div>
  )

  if (title) {
    return (
      <div className={cn('rounded-2xl bg-white/[0.04] border border-white/[0.08] p-4', className)}>
        <p className="text-sm font-medium text-white/60 mb-3">{title}</p>
        {chart}
      </div>
    )
  }
  return chart
}

// ---- BarChart ----
interface BarChartProps {
  // Option A: Chart.js data object (preferred in views)
  data?: ChartData
  title?: string
  // Option B: flat props (backwards compat)
  labels?: string[]
  barData?: number[]
  color?: string
  height?: number
  className?: string
}

export function BarChart({ data, title, labels, barData, color = COLORS.sky, height = 200, className }: BarChartProps) {
  const resolvedLabels = data?.labels ?? labels ?? []
  const resolvedDatasets = data
    ? data.datasets.map(d => ({
        ...d,
        borderRadius: d.borderRadius ?? 6,
        borderWidth: d.borderWidth ?? 1,
      }))
    : [{
        data: barData ?? [],
        backgroundColor: `${color}40`,
        borderColor: color,
        borderWidth: 1,
        borderRadius: 6,
      }]

  const chart = (
    <div className={cn('w-full', !title && className)} style={{ height }}>
      <Bar options={baseOptions as any} data={{ labels: resolvedLabels, datasets: resolvedDatasets }} />
    </div>
  )

  if (title) {
    return (
      <div className={cn('rounded-2xl bg-white/[0.04] border border-white/[0.08] p-4', className)}>
        <p className="text-sm font-medium text-white/60 mb-3">{title}</p>
        {chart}
      </div>
    )
  }
  return chart
}

// ---- DonutChart ----
interface DonutChartProps {
  // Option A: Chart.js data object
  data?: ChartData
  title?: string
  height?: number
  // Option B: flat props
  labels?: string[]
  values?: number[]
  colors?: string[]
  size?: number
  className?: string
}

export function DonutChart({ data, title, height = 160, labels, values, colors, size, className }: DonutChartProps) {
  const resolvedLabels = data?.labels ?? labels ?? []
  const rawDataset = data?.datasets[0]
  const resolvedValues = (rawDataset?.data as number[] | undefined) ?? values ?? []
  const resolvedColors = (rawDataset?.backgroundColor as string[] | undefined) ?? colors
  const palette = resolvedColors ?? [COLORS.sky, COLORS.emerald, COLORS.blue, COLORS.indigo]
  const resolvedSize = size ?? height

  const chart = (
    <div style={{ width: '100%', height: resolvedSize }}>
      <Doughnut
        options={{ ...baseOptions, scales: undefined, cutout: '70%' } as any}
        data={{
          labels: resolvedLabels,
          datasets: [{
            data: resolvedValues,
            backgroundColor: rawDataset?.backgroundColor ?? palette.map(c => `${c}80`),
            borderColor: rawDataset?.borderColor ?? palette,
            borderWidth: rawDataset?.borderWidth ?? 2,
          }],
        }}
      />
    </div>
  )

  if (title) {
    return (
      <div className={cn('rounded-2xl bg-white/[0.04] border border-white/[0.08] p-4', className)}>
        <p className="text-sm font-medium text-white/60 mb-3">{title}</p>
        {chart}
      </div>
    )
  }
  return <div className={className}>{chart}</div>
}
